/**
 * ダッシュボードページ - melta UI
 * 統計カード、ライフサイクル分布、接続推移、最近のイベント
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { deviceApi } from '../services/api';
import { Icon } from '../components/icons';
import { Button, Card, Alert, Badge } from '../components/ui';

const STATUS_CONFIG = {
  active: { label: 'Active', color: 'bg-emerald-500' },
  registered: { label: 'Registered', color: 'bg-primary-500' },
  maintenance: { label: 'Maintenance', color: 'bg-amber-500' },
  retired: { label: 'Retired', color: 'bg-slate-500' },
  unknown: { label: 'Unknown', color: 'bg-slate-300' },
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const data = await deviceApi.list({ maxResults: '200' });
      setDevices(data.devices || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // 統計計算
  const stats = devices.reduce(
    (acc, d) => {
      const s = d.lifecycleStatus || 'unknown';
      acc[s] = (acc[s] || 0) + 1;
      acc.total++;
      if (d.connectionStatus === 'connected') acc.online++;
      else acc.offline++;
      // 証明書期限チェック（30日以内）
      if (d.certificateExpiry) {
        const expiry = new Date(d.certificateExpiry);
        const daysLeft = (expiry - new Date()) / (1000 * 60 * 60 * 24);
        if (daysLeft < 30 && daysLeft > 0) acc.certExpiring++;
      }
      return acc;
    },
    { total: 0, online: 0, offline: 0, certExpiring: 0 }
  );

  // ライフサイクル分布データ
  const lifecycleData = Object.entries(STATUS_CONFIG).map(([key, config]) => ({
    key,
    label: config.label,
    color: config.color,
    count: stats[key] || 0,
    percentage: stats.total > 0 ? Math.round(((stats[key] || 0) / stats.total) * 100) : 0,
  })).filter(d => d.count > 0);

  // 最近接続/切断されたデバイス
  const recentDevices = [...devices]
    .filter(d => d.lastConnectedAt || d.lastDisconnectedAt)
    .sort((a, b) => {
      const aTime = a.lastConnectedAt || a.lastDisconnectedAt || '';
      const bTime = b.lastConnectedAt || b.lastDisconnectedAt || '';
      return bTime.localeCompare(aTime);
    })
    .slice(0, 10);

  if (loading) {
    return (
      <div className="space-y-6">
        <p className="text-sm text-slate-500">IoT Device Management</p>
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-slate-500">データを読み込み中...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <p className="text-sm text-slate-500">IoT Device Management</p>

      {error && (
        <Alert variant="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* 統計カード */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card padding="p-5">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
              <Icon name="wifi" className="w-6 h-6 text-emerald-500" />
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-900">{stats.online}</div>
              <div className="text-sm text-slate-500">オンライン</div>
            </div>
          </div>
        </Card>

        <Card padding="p-5">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
              <Icon name="wifiOff" className="w-6 h-6 text-red-500" />
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-900">{stats.offline}</div>
              <div className="text-sm text-slate-500">オフライン</div>
            </div>
          </div>
        </Card>

        <Card padding="p-5">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center">
              <Icon name="device" className="w-6 h-6 text-primary-500" />
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-900">{stats.total}</div>
              <div className="text-sm text-slate-500">全機体</div>
            </div>
          </div>
        </Card>

        <Card padding="p-5">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
              <Icon name="alertTriangle" className="w-6 h-6 text-amber-500" />
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-900">{stats.certExpiring}</div>
              <div className="text-sm text-slate-500">証明書期限切迫</div>
            </div>
          </div>
        </Card>
      </div>

      {/* チャートグリッド */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* ライフサイクル分布 */}
        <Card title="ライフサイクル分布">
          {lifecycleData.length > 0 ? (
            <div className="space-y-4">
              {/* プログレスバー */}
              <div className="h-3 rounded-full bg-slate-100 overflow-hidden flex">
                {lifecycleData.map((d) => (
                  <div
                    key={d.key}
                    className={`${d.color} transition-all`}
                    style={{ width: `${d.percentage}%` }}
                    title={`${d.label}: ${d.count} (${d.percentage}%)`}
                  />
                ))}
              </div>
              {/* 凡例 */}
              <div className="flex flex-wrap gap-3">
                {lifecycleData.map((d) => (
                  <div key={d.key} className="flex items-center gap-2">
                    <span className={`w-3 h-3 rounded-full ${d.color}`} />
                    <span className="text-sm text-slate-600">{d.label}</span>
                    <span className="text-sm font-medium text-slate-900">{d.count}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              機体が登録されていません
            </div>
          )}
        </Card>

        {/* Thing Type 分布 */}
        <Card title="機体タイプ分布">
          <div className="space-y-3">
            {(() => {
              const typeCount = {};
              devices.forEach(d => {
                const t = d.thingTypeName || '未設定';
                typeCount[t] = (typeCount[t] || 0) + 1;
              });
              const entries = Object.entries(typeCount).sort((a, b) => b[1] - a[1]);

              if (entries.length === 0) {
                return (
                  <div className="text-center py-8 text-slate-500">
                    データなし
                  </div>
                );
              }

              return entries.map(([type, count]) => (
                <div key={type} className="flex items-center gap-3">
                  <span className="text-sm text-slate-700 w-24 truncate">{type}</span>
                  <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                    <div
                      className="h-full bg-primary-500 rounded-full transition-all"
                      style={{ width: `${(count / stats.total) * 100}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium text-slate-900 w-8 text-right">{count}</span>
                </div>
              ));
            })()}
          </div>
        </Card>
      </div>

      {/* 最近のイベント */}
      <Card
        title="最近の機体アクティビティ"
        headerActions={
          <Button variant="secondary" size="sm" onClick={() => navigate('/devices')}>
            全機体を見る
            <Icon name="chevronRight" className="w-4 h-4" />
          </Button>
        }
      >
        {recentDevices.length > 0 ? (
          <div className="divide-y divide-slate-100">
            {recentDevices.map((device) => {
              const isConnected = device.connectionStatus === 'connected';
              const time = device.lastConnectedAt || device.lastDisconnectedAt;
              return (
                <div
                  key={device.thingName}
                  className="flex items-center gap-4 py-3 px-2 -mx-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => navigate(`/devices/${device.thingName}`)}
                >
                  <div className={`w-2.5 h-2.5 rounded-full ${isConnected ? 'bg-emerald-500' : 'bg-red-500'}`} />
                  <span className="font-medium text-slate-900 flex-1 truncate">
                    {device.thingName}
                  </span>
                  {device.thingTypeName && (
                    <Badge variant="primary" size="sm">
                      {device.thingTypeName}
                    </Badge>
                  )}
                  <Badge variant={isConnected ? 'success' : 'danger'} size="sm">
                    {isConnected ? 'Connected' : 'Disconnected'}
                  </Badge>
                  <span className="text-sm text-slate-500 w-40 text-right">
                    {time ? new Date(time).toLocaleString('ja-JP') : '—'}
                  </span>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-8 text-slate-500">
            アクティビティはまだありません
          </div>
        )}
      </Card>
    </div>
  );
}
